import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { apiSend } from "../../api";
import { date, money } from "../../format";
import { Modal } from "./Modal";
import { useTicketContext } from "./useTicket";

export const cardLabel = (c: { cardType: string | null; last4: string | null }) => `${c.cardType ?? "Card"} ending ${c.last4 ?? "????"}`;

/** Opens Authorize.Net's own card page in a new tab (card numbers never touch the portal). */
async function openHostedCardPage(open: () => Promise<{ url: string; token: string }>) {
  const tab = window.open("", "_blank");
  try {
    const { url, token } = await open();
    if (!tab) throw new Error("The browser blocked the new tab. Allow pop-ups for the portal.");
    const doc = tab.document;
    doc.title = "Opening Authorize.Net…";
    const form = doc.createElement("form");
    form.method = "POST";
    form.action = url;
    const input = doc.createElement("input");
    input.type = "hidden";
    input.name = "token";
    input.value = token;
    form.appendChild(input);
    doc.body.appendChild(form);
    form.submit();
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/** The ticket's card: show it, add or change it in Authorize.Net's form, refresh, remove. */
export function CardDialog({ onClose }: { onClose: () => void }) {
  const { config, detail, save, saving } = useTicketContext();
  const auth = useAuth();
  const [error, setError] = useState("");
  const [opened, setOpened] = useState(false);
  const c = detail.card;
  const no = detail.ticket.ticketNo;
  const start = () => {
    setError("");
    openHostedCardPage(() => apiSend<{ url: string; token: string }>(config, auth.user?.access_token ?? "", "POST", `/tickets/${no}/card-session`))
      .then(() => setOpened(true)).catch((e: Error) => setError(e.message));
  };
  return (
    <Modal title="Card on file" onClose={onClose}>
      <div className="mini-form">
        {c ? (
          <div className="card-info">
            <strong>{cardLabel(c)}</strong>{c.expiration && c.expiration !== "XXXX" && <> · expires {c.expiration}</>}
            {c.billing && <div className="hint">{[[c.billing.firstName, c.billing.lastName].filter(Boolean).join(" "), c.billing.address, [c.billing.city, c.billing.state, c.billing.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>}
            {c.environment === "production"
              ? <p className="warn">Saved in the old system (live Authorize.Net). Until go-live it can't be charged or removed here.</p>
              : <p className="hint">Authorize.Net sandbox (test card).</p>}
          </div>
        ) : <p className="muted">No card on file for this ticket.</p>}
        <p className="hint">Cards are entered in Authorize.Net's own secure form; the portal never sees the card number. In the sandbox use a test card such as 4111 1111 1111 1111, any future date, any 3-digit code.</p>
        {error && <p className="warn">{error}</p>}
        <div className="btn-row wrap">
          <button type="button" className="btn primary" disabled={saving} onClick={start}>{c ? "Change or add a card" : "Add a card"}</button>
          {opened && <button type="button" className="btn outline" disabled={saving} onClick={() => void save("POST", "/card-sync").then((d) => d && setOpened(false))}>I'm done: update from Authorize.Net</button>}
          {c && c.environment === "sandbox" && (
            <button type="button" className="btn outline danger" disabled={saving}
              onClick={() => { if (window.confirm(`Remove the ${cardLabel(c)} from Authorize.Net? It can't be charged afterwards.`)) void save("POST", "/card-delete"); }}>Remove card</button>
          )}
          <button type="button" className="btn outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  );
}

/** Back from Authorize.Net's form (the page reopens with ?card=done): read the cards back once. */
export function useCardReturn() {
  const { save } = useTicketContext();
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("card") !== "done") return;
    u.searchParams.delete("card");
    window.history.replaceState(null, "", u.toString());
    void save("POST", "/card-sync");
  }, [save]);
}

/** Refund a card payment, full or partial (owner: customer emailed, status unchanged). */
export function RefundDialog({ onClose }: { onClose: () => void }) {
  const { detail, save, saving } = useTicketContext();
  const charges = detail.payments.filter((p) => p.refundable && Number(p.refundable) > 0);
  const [paymentId, setPaymentId] = useState(charges[0]?.id ?? "");
  const chosen = charges.find((p) => p.id === paymentId);
  const [amount, setAmount] = useState(chosen?.refundable ?? "");
  return (
    <Modal title="Refund" onClose={onClose}>
      {charges.length === 0 ? <p className="muted">No card payments on this ticket to refund.</p> : (
        <form className="mini-form" onSubmit={async (e) => {
          e.preventDefault();
          if (!window.confirm(`Refund ${money(amount)} to the customer's card? This can't be undone.`)) return;
          if (await save("POST", "/refunds", { paymentId, amount: Number(amount) })) onClose();
        }}>
          <fieldset className="radios"><legend className="lbl">Which payment?</legend>
            {charges.map((p) => (
              <label key={p.id}><input type="radio" name="charge" checked={p.id === paymentId}
                onChange={() => { setPaymentId(p.id); setAmount(p.refundable ?? ""); }} />
                {date(p.paidAt ?? p.createdAt)} · {money(p.amount)}{Number(p.refundable) < Number(p.amount) ? ` (${money(p.refundable)} left)` : ""}</label>
            ))}
          </fieldset>
          <label>Amount to refund ($)<input type="number" min="0.01" step="0.01" max={chosen?.refundable ?? undefined} required value={amount}
            onChange={(e) => setAmount(e.target.value)} autoFocus /></label>
          <p className="hint">The customer is emailed; the status does not change. A charge from today that hasn't settled yet can only be refunded in full (it is cancelled).</p>
          <div className="btn-row"><button type="submit" className="btn primary" disabled={saving}>Refund</button>
            <button type="button" className="btn outline" onClick={onClose}>Cancel</button></div>
        </form>
      )}
    </Modal>
  );
}
